import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import type { WiseBalance } from '@/lib/wiseService';

const CURRENCY_FLAGS: Record<string, string> = {
  USD: 'US',
  EUR: 'EU',
  GBP: 'GB',
  MVR: 'MV',
  AUD: 'AU',
  CAD: 'CA',
  SGD: 'SG',
  JPY: 'JP',
  INR: 'IN',
  AED: 'AE',
  CHF: 'CH',
  NZD: 'NZ',
  HKD: 'HK',
  MYR: 'MY',
  THB: 'TH',
  LKR: 'LK',
};

function getFlagEmoji(currencyCode: string): string {
  const countryCode = CURRENCY_FLAGS[currencyCode];
  if (!countryCode) return '';
  if (countryCode === 'EU') return '\u{1F1EA}\u{1F1FA}';
  const codePoints = [...countryCode].map(
    (c) => 0x1f1e6 + c.charCodeAt(0) - 65
  );
  return String.fromCodePoint(...codePoints);
}

function formatBalanceValue(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

interface BalanceCardProps {
  balance: WiseBalance;
  isSelected: boolean;
  onClick: () => void;
}

export default function BalanceCard({ balance, isSelected, onClick }: BalanceCardProps) {
  const amount = balance.amount.value;
  const reserved = balance.reservedAmount?.value || 0;
  const isPositive = amount > 0;
  const flag = getFlagEmoji(balance.currency);

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-2xl p-5 transition-all duration-300 ${
        isSelected
          ? 'bg-emerald-800 shadow-lg shadow-emerald-900/20 scale-[1.02]'
          : 'glass-panel hover:shadow-lg hover:-translate-y-0.5'
      }`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg ${
            isSelected ? 'bg-white/20' : 'bg-white/10'
          }`}>
            {flag || balance.currency.charAt(0)}
          </div>
          <div>
            <p className={`text-xs font-medium ${isSelected ? 'text-white/70' : 'text-white'}`}>
              {balance.name || balance.currency}
            </p>
            <p className={`text-[11px] ${isSelected ? 'text-white/50' : 'text-white'}`}>
              {balance.type === 'SAVINGS' ? 'Jar' : 'Balance'}
            </p>
          </div>
        </div>
        <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
          isSelected
            ? 'bg-white/20'
            : isPositive ? 'bg-emerald-500/10' : 'bg-red-500/10'
        }`}>
          {isPositive
            ? <ArrowUpRight size={14} className={isSelected ? 'text-white' : 'text-emerald-800'} />
            : <ArrowDownRight size={14} className={isSelected ? 'text-white' : 'text-red-600'} />
          }
        </div>
      </div>

      <p className={`text-xl font-bold tracking-tight tabular-nums ${
        isSelected ? 'text-white' : 'text-white'
      }`}>
        {formatBalanceValue(amount, balance.currency)}
      </p>

      {reserved > 0 && (
        <p className={`text-[11px] mt-1 ${isSelected ? 'text-white/60' : 'text-white'}`}>
          Reserved: {formatBalanceValue(reserved, balance.currency)}
        </p>
      )}
    </button>
  );
}
